import { countryDialCodes } from "@/components/Inputs/PhoneInput/countryDialCodes";
import { MutableRefObject, useEffect } from "react";
import { FieldValues, Path, UseFormWatch } from "react-hook-form";

interface UsePhoneRehydrateOptions<T extends FieldValues = FieldValues> {
  name: Path<T>;
  watch: UseFormWatch<T>;
  onPrefixSelect: (iso3: string) => void;
  onRawNumberChange: (rawNumber: string) => void;
  isInitialized: MutableRefObject<boolean>;
}

/**
 * On mount, parses the stored combined phone value (e.g. "+38591234567")
 * back into dial code prefix + raw number so the input is pre-filled
 * when navigating back to the form.
 */
export const usePhoneRehydrate = <T extends FieldValues = FieldValues>({
  name,
  watch,
  onPrefixSelect,
  onRawNumberChange,
  isInitialized,
}: UsePhoneRehydrateOptions<T>) => {
  useEffect(() => {
    const stored = watch(name) as string | undefined;
    if (stored?.startsWith("+")) {
      const match = Object.entries(countryDialCodes)
        .filter(([, entry]) => stored.startsWith(entry.dialCode))
        .sort((a, b) => b[1].dialCode.length - a[1].dialCode.length)[0];

      if (match) {
        const [iso3, entry] = match;
        onPrefixSelect(iso3);
        onRawNumberChange(stored.slice(entry.dialCode.length));
      }
    }
    isInitialized.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
